import { useEffect, useState, type FormEvent } from "react";
import { LockKeyhole } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BusyLabel, InlineAlert } from "@/components/admin/feedback";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { containsProductKey } from "@/lib/product-key-redaction";

export function RevokeProductKeyDialog({
  open,
  onOpenChange,
  keyID,
  busy,
  error,
  onRevoke,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keyID: string;
  busy: boolean;
  error: string | null;
  onRevoke: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [validation, setValidation] = useState<string | null>(null);

  useEffect(() => {
    if (open) return;
    setReason("");
    setValidation(null);
  }, [open]);

  function close(next: boolean) {
    if (!next && busy) return;
    if (!next) {
      setReason("");
      setValidation(null);
    }
    onOpenChange(next);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!reason.trim()) {
      setValidation(
        t(
          "dialogs.revokeProductKeyReasonRequired",
          "A reason is required to revoke this Product Key.",
        ),
      );
      return;
    }
    if (containsProductKey(reason)) {
      setValidation(
        t(
          "dialogs.revokeProductKeyReasonContainsKey",
          "Reason must not contain a Product Key.",
        ),
      );
      return;
    }
    setValidation(null);
    onRevoke(reason.trim());
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="border-danger/35 sm:max-w-md" showClose={!busy}>
        <form onSubmit={submit}>
          <DialogHeader>
            <div className="flex items-center gap-2 text-danger">
              <LockKeyhole className="size-5" aria-hidden="true" />
              <DialogTitle className="text-base font-semibold text-danger">
                {t("dialogs.revokeProductKeyTitle", "Revoke Product Key")}
              </DialogTitle>
            </div>
            <DialogDescription className="mt-2 text-xs leading-5 text-muted">
              {t(
                "dialogs.revokeProductKeyConfirm",
				"This credential can no longer create new Activations. Its linked active Device, if any, will be deactivated.",
              )}
              <span className="mt-1 block font-mono text-ink">{keyID}</span>
            </DialogDescription>
          </DialogHeader>

          <p className="mt-3 text-xs leading-5 text-muted">
            {t(
              "dialogs.deactivateActivationOfflineConsequence",
              "This is not an immediate remote kill. A signed offline token may continue to work until its bounded expiry or offline window ends.",
            )}
          </p>

          <div className="mt-4 space-y-2">
            <Label htmlFor="revoke-product-key-reason" className="text-xs">
              {t("dialogs.reason", "Reason")} <span className="text-danger">*</span>
            </Label>
            <Input
              id="revoke-product-key-reason"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setValidation(null);
              }}
              aria-invalid={Boolean(validation || error)}
              aria-describedby={validation || error ? "revoke-product-key-reason-error" : undefined}
              placeholder={t(
                "dialogs.revokeProductKeyReasonPlaceholder",
                "e.g. credential was exposed",
              )}
              autoFocus
              disabled={busy}
            />
          </div>

          {(validation || error) && (
            <div id="revoke-product-key-reason-error" className="mt-3">
              <InlineAlert
                title={t("search.details.actionBlocked", "Action blocked")}
                message={validation || error!}
              />
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => close(false)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              variant="danger"
              size="sm"
              disabled={!reason.trim() || busy}
            >
              {busy ? (
                <BusyLabel>{t("dialogs.revoking", "Revoking")}</BusyLabel>
              ) : (
                t("dialogs.confirmRevoke", "Revoke")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
